<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import Icon from '@/Components/UI/Icon.vue'
import LiveMap from '@/Components/GPS/LiveMap.vue'
import AlertHistory from '@/Components/GPS/AlertHistory.vue'
import UiButton from '@/Components/UI/UiButton.vue'

defineOptions({ layout: AppLayout })

const props = defineProps({
  vehicle:         { type: Object, required: true },
  traccar_devices: { type: Array,  default: () => [] },
  position:        { type: Object, default: null },
  alerts:          { type: Array,  default: () => [] },
  error:           { type: String, default: null },
})

const { t } = useI18n()
const livePosition = ref(props.position)
let   pollInterval = null

// History-aware back: returns to wherever the user came from (fleet map,
// vehicle show, …). The old hardcoded link to /vehicles/:id sent users
// FORWARD to the show page, whose own back chevron then bounced to here —
// an inescapable GPS<->show loop when arriving from the fleet map.
const goBack = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) window.history.back()
  else router.visit(`/vehicles/${props.vehicle.id}`)
}

async function fetchPosition() {
  if (!props.vehicle.traccar_device_id) return
  try {
    const res  = await fetch(`/gps/positions?deviceId=${props.vehicle.traccar_device_id}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    const data = await res.json()
    const pos  = (data.positions || []).find(p => String(p.deviceId) === String(props.vehicle.traccar_device_id))
    if (pos) livePosition.value = pos
  } catch { /* keep last known */ }
}

onMounted(() => { pollInterval = setInterval(fetchPosition, 15_000) })
onBeforeUnmount(() => { clearInterval(pollInterval) })

const linkForm = useForm({ vehicle: { traccar_device_id: props.vehicle.traccar_device_id || '' } })
function submitLink() {
  linkForm.patch(`/vehicles/${props.vehicle.id}/link_device`)
}
</script>

<template>
  <PageShell :title="t('nav.fleet')">
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
        :aria-label="t('common.back')"
        @click="goBack"
      >
        <Icon name="chev-l" :size="18" />
      </button>
      <h2 class="text-xl font-semibold text-[color:var(--ink)] truncate flex items-center gap-2">
        <span>{{ vehicle.make }} {{ vehicle.model }}</span>
        <span class="text-sm font-normal text-[color:var(--muted)]">GPS Tracking</span>
      </h2>
    </div>

    <div
      v-if="error"
      class="rounded-[var(--radius-md)] px-4 py-3 text-sm"
      :style="{ background: 'var(--dangerSoft)', border: '1px solid color-mix(in oklab, var(--danger) 35%, transparent)', color: 'var(--danger)' }"
    >
      {{ error }}
    </div>

    <!-- Device linker -->
    <div class="bg-[color:var(--surface)] rounded-[var(--radius-lg)] border border-[color:var(--line)] p-5">
      <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-3">GPS Tracker</h2>
      <form @submit.prevent="submitLink" class="flex flex-col md:flex-row md:items-center gap-3">
        <select
          v-model="linkForm.vehicle.traccar_device_id"
          class="flex-1 border border-[color:var(--line)] rounded-[var(--radius-md)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
        >
          <option value="">— No tracker —</option>
          <option v-for="d in traccar_devices" :key="d.id" :value="d.id">
            {{ d.name }} ({{ d.status }})
          </option>
        </select>
        <UiButton
          type="submit"
          variant="primary"
          :loading="linkForm.processing"
          class="md:w-auto"
        >
          Save
        </UiButton>
      </form>
      <p v-if="traccar_devices.length === 0" class="mt-2 text-xs text-[color:var(--muted)]">
        No devices found in Traccar. Register the GT06 device in the Traccar admin panel first.
      </p>
    </div>

    <!-- Live map -->
    <div v-if="vehicle.traccar_device_id">
      <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-2">Current Position</h2>
      <div class="-mx-6 md:mx-0">
        <LiveMap
          :positions="livePosition ? [livePosition] : []"
          :vehicles="[vehicle]"
          :zoom="15"
          class="!h-[60vh] md:!h-[500px] md:!rounded-[var(--radius-lg)]"
        />
      </div>
    </div>
    <div
      v-else
      class="rounded-[var(--radius-lg)] p-8 text-center text-sm text-[color:var(--muted)]"
      :style="{ background: 'var(--surfaceAlt)', border: '1px dashed var(--line)' }"
    >
      Link a GPS tracker above to see the live position.
    </div>

    <!-- Alert history -->
    <AlertHistory :alerts="alerts" />
  </PageShell>
</template>
